import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { useFetchSessionRequest, usePolling } from 'mmfintech-checkout-commons'
import { findCurrencyPrecision, formatFloat, formatMoney, tr } from 'mmfintech-commons'

import { Button } from 'mmfintech-portal-commons'
import { QRCodeSVG } from 'qrcode.react'
import { LabelValue, WarningMessage } from '../../../components'
import { CryptoPreviewTitle, CryptoPreviewWrapper } from './PreviewCrypto.styled'

type PreviewCryptoProps = {
  sessionId?: string
  data?: any
  onBack?: () => void
  onClose?: () => void
}

const STATUS_POLLING_TIMEOUT = 30000 // 30 seconds

export const PreviewCrypto = ({ sessionId, data, onClose, onBack }: PreviewCryptoProps) => {
  const { address, currency, tag, processingAmount } = data || {}

  const history = useHistory()
  const polling = usePolling(() => reloadSessionStatus())

  const { fetchSession } = useFetchSessionRequest()

  const handleStatusFetchSuccess = (response: any): void => {
    const { status } = response?.data || {}

    switch (status) {
      case 'PROCESSED':
      case 'CONFIRMED':
        history.push('/success')
        break

      case 'FAILED':
      case 'CANCELLED':
        history.push('/fail')
        break

      default:
        polling.start(STATUS_POLLING_TIMEOUT)
    }
  }

  const handleStatusFetchFail = () => {
    polling.start(STATUS_POLLING_TIMEOUT)
  }

  const reloadSessionStatus = () => {
    fetchSession(sessionId, handleStatusFetchSuccess, handleStatusFetchFail)
  }

  useEffect(() => {
    if (sessionId) {
      polling.start(STATUS_POLLING_TIMEOUT)
    }
  }, [])

  return (
    <>
      <CryptoPreviewWrapper>
        <CryptoPreviewTitle>{tr('CHECKOUT.DEPOSIT.CRYPTO_DETAILS.TITLE', 'Details')}</CryptoPreviewTitle>

        <LabelValue
          label={tr('CHECKOUT.DEPOSIT.CRYPTO_DETAILS.ADDRESS', 'Deposit address')}
          value={address}
          enableCopy={true}
          dataTest='address'
        />

        <p className='text-center code bordered'>
          <QRCodeSVG value={address} size={150} />
        </p>

        <LabelValue
          label={tr('CHECKOUT.DEPOSIT.CRYPTO_DETAILS.TAG', 'Tag')}
          value={tag}
          dataTest='tag'
          enableCopy={true}
        />

        <LabelValue
          label={tr('CHECKOUT.BANKING.COMMON.AMOUNT_LABEL', 'Amount')}
          value={formatMoney(processingAmount, currency)}
          dataTest='amount'
          enableCopy={true}
          copyValue={formatFloat(processingAmount, findCurrencyPrecision(currency))}
        />

        <div className='mb-2' />

        {typeof onClose === 'function' && (
          <Button
            type='button'
            color='primary'
            text={tr('CHECKOUT.BUTTONS.CLOSE', 'Close')}
            onClick={onClose}
            data-test='button-close'
          />
        )}
        {typeof onBack === 'function' && (
          <Button
            type='button'
            color='primary'
            text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
            onClick={onBack}
            data-test='button-back'
          />
        )}
      </CryptoPreviewWrapper>

      <WarningMessage currency={currency} />
    </>
  )
}
